import * as R from 'ramda'
import { Offer, EquipmentContract } from '../../types/types'
import { previousContractEquipment } from './opportunity'
import moment from 'moment'

const getOfferBuildingDetails = (offer: Offer) => offer.buildings.map(({ details }) => details)

const getAllEquipmentIds = (offer: Offer) => {
  const buildings = getOfferBuildingDetails(offer)
  return R.chain(({ equipment }) => equipment.map(e => e.id), buildings)
}

const previousContractEquipmentOnCurrentOffer = (offer: Offer): EquipmentContract[] => {
  const includedEquipmentIds = getAllEquipmentIds(offer)
  return Object.values(previousContractEquipment(offer.opportunity)).filter(e => includedEquipmentIds.includes(e.id))
}

export const previousContractEquipmentOnCurrentOfferAsRecord = (offer: Offer): Record<string, EquipmentContract> =>
  R.indexBy(e => e.id, previousContractEquipmentOnCurrentOffer(offer))

export const contractsAnnualPrice = (offer: Offer) =>
  R.sum(previousContractEquipmentOnCurrentOffer(offer).map(e => e.annualValue))

export const getEquipmentDataFromPreviousContract = (offer: Offer, equipmentId: null | string) =>
  equipmentId ? previousContractEquipmentOnCurrentOffer(offer).find(e => e.id === equipmentId) : undefined

export const isFuturePricesEnabled = (offer: Offer): boolean =>
  (isCommercialTermFuturePrices(offer) && getFuturePricesYears(getValidPeriod(offer)) > 1) ||
  isFutureProportionalPrice(offer)

export const isStartBeforeAcceptanceDates = (
  contractStartDate: string | undefined,
  contractAcceptanceDate: string | undefined
): boolean => {
  const startDate = toCommercialTermDateMoment(contractStartDate)
  const acceptanceDate = toCommercialTermDateMoment(contractAcceptanceDate)
  return startDate != null && acceptanceDate != null && startDate.isSameOrBefore(acceptanceDate)
}

export const isCommercialTermFuturePrices = (offer: Offer): boolean => {
  const arr = ['01', '02', '08', 'Z8']
  return checkFuturePriceGroup(offer.commercialTerms?.KONDA) && !arr.includes(getValidPeriod(offer) || '')
}

export const checkFuturePriceGroup = (sapCode?: string): boolean => {
  const validFuturePriceSapKeys = ['K2', 'FP']
  return validFuturePriceSapKeys.includes(sapCode || '')
}

export function getValidPeriod(offer: Offer): string | undefined {
  return offer.commercialTerms?.VLAUFK
}

export const isProportionalPrice = (offer: Offer): string | undefined => offer.commercialTerms?.PROPORTIONAL_PRICE

function checkStartAcceptanceDateValid(startDateVal: any, acceptanceDateVal: any) {
  return toCommercialTermDateMoment(startDateVal) != null && toCommercialTermDateMoment(acceptanceDateVal) != null
}

function toCommercialTermDateMoment(dateVal: any): moment.Moment | null {
  if (typeof dateVal !== 'string') {
    return null
  }

  const parsedDate = moment(dateVal, 'YYYY-MM-DD', true)
  return parsedDate.isValid() ? parsedDate : null
}

function isProportionalPriceEnabled(offer: Offer): boolean {
  const { PROPORTIONAL_PRICE, VBEGDAT, VABNDAT } = offer.commercialTerms || {}
  return PROPORTIONAL_PRICE === '1' && checkStartAcceptanceDateValid(VBEGDAT as string, VABNDAT as string)
}

export const isFutureProportionalPrice = (offer: Offer): boolean => {
  const startDate = offer.commercialTerms?.VBEGDAT as string
  const acceptanceDate = offer.commercialTerms?.VABNDAT as string
  return (
    checkStartAcceptanceDateValid(startDate, acceptanceDate) &&
    checkFuturePriceGroup(offer.commercialTerms?.KONDA) &&
    offer.commercialTerms?.PROPORTIONAL_PRICE === '1'
  )
}

interface PriceEstimationConfiguration {
  isOnlyFP: boolean
  isOnlyPP: boolean
  isFPP: boolean
  isOnlyPPInLegacyFPP: boolean
}

export const getPriceEstimationConfiguration = (offer: Offer): PriceEstimationConfiguration => {
  const isFPInCT = checkFuturePriceGroup(offer.commercialTerms?.KONDA)
  const isValidityPeriodForFP = getFuturePricesYears(getValidPeriod(offer)) > 1
  const isValidPPInCT = isProportionalPriceEnabled(offer)
  const hasRowsInPriceEstimation = Array.isArray(offer.priceEstimation) && offer.priceEstimation.length > 0
  return {
    isOnlyFP: !isValidPPInCT && isFPInCT && isValidityPeriodForFP,
    isOnlyPP: !isFPInCT && isValidPPInCT,
    isFPP: isValidPPInCT && isFPInCT,
    isOnlyPPInLegacyFPP: isValidPPInCT && isFPInCT && !hasRowsInPriceEstimation
  }
}

const validPeriodCodes: Record<string, number> = {
  '02': 1,
  '03': 2,
  '04': 3,
  '05': 4,
  '06': 5,
  Z6: 6,
  Z1: 7,
  '11': 8,
  '12': 9,
  '07': 10,
  '10': 15,
  '09': 20,
  Z2: 25,
  '08': 0.25,
  '01': 0.5,
  Z8: 0.08
  // To do in MBS for period code value less than zero
}

export const getFuturePricesYears = (validPeriod: string | undefined): number =>
  validPeriod != null && validPeriodCodes[validPeriod] >= 1 ? validPeriodCodes[validPeriod] || 0 : 0

export const getFuturePricesBaseExcluded = (validPeriod: string | undefined): number =>
  validPeriod != null && validPeriodCodes[validPeriod] >= 1 ? (validPeriodCodes[validPeriod] || 0) - 1 : 0

export const isPeopleFlow = (offer: Offer) => {
  const equipmentGroupValues = Object.values(offer.equipmentGroups)
  const peopleFlowGroups = equipmentGroupValues.filter(group => group.groupKind === 'people_flow')
  const hasPeopleFlowGroup = peopleFlowGroups.length > 0
  const isOnlyPeopleFlowOffer = hasPeopleFlowGroup && peopleFlowGroups.length === equipmentGroupValues.length
  return {
    hasPeopleFlowGroup,
    isOnlyPeopleFlowOffer
  }
}

export const doesOfferSufficeInteraction = (offer: Offer) =>
  !!offer.selectedInteractionRating || isPeopleFlow(offer).isOnlyPeopleFlowOffer || offer.isEscalationFlow.active

export const getGroupNameById = (offer: Offer, groupId: string) =>
  R.pathOr('', ['equipmentGroups', groupId, 'name'], offer)
